import React, { useEffect, useState } from "react";

import { ordersService } from "api/services/orders.service";
import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import Pagination from "components/UI/Pagination";
import Table, { Column } from "components/UI/Table";

import type { AdminOrder } from "api/types/order.types";
import "pages/AdminPage.scss";

const STATUS_BADGE: Record<string, "active" | "pending" | "rejected" | "info" | "draft"> = {
  CONFIRMED: "active",
  COMPLETED: "active",
  PENDING: "pending",
  PAYMENT_ATTEMPTED: "pending",
  PAYMENT_PROCESSING: "pending",
  PAYMENT_FAILED: "rejected",
  CANCELLED: "draft",
};

const PRODUCT_TYPE_LABEL: Record<string, string> = {
  LESSON: "수강권",
  TRAVEL: "투어",
  PRODUCT: "제품",
};

const PAGE_SIZE = 10;

const PaymentManagement: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchOrders = async (page: number, status: string, q: string, from: string, to: string) => {
    setLoading(true);
    try {
      const res = await ordersService.list({
        page,
        page_size: PAGE_SIZE,
        ...(status ? { status: status as AdminOrder["status"] } : {}),
        ...(q ? { search: q } : {}),
        ...(from ? { date_from: from } : {}),
        ...(to ? { date_to: to } : {}),
      });
      setOrders(res.data.results ?? []);
      setTotal(res.data.count ?? 0);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders(currentPage, statusFilter, search, dateFrom, dateTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchOrders(1, statusFilter, search, dateFrom, dateTo);
  };

  const paidCount = orders.filter((o) => o.status === "CONFIRMED" || o.status === "COMPLETED").length;
  const refundedCount = orders.filter((o) => o.status === "CANCELLED").length;
  const totalAmount = orders.filter((o) => o.status === "CONFIRMED" || o.status === "COMPLETED").reduce((sum, o) => sum + Number(o.total_amount), 0);

  const columns: Column<AdminOrder>[] = [
    { key: "order_number", title: "결제 UUID", width: "200px" },
    {
      key: "user",
      title: "결제자",
      width: "100px",
      render: (v) => (v as AdminOrder["user"])?.name ?? "-",
    },
    {
      key: "product_type",
      title: "결제 품목",
      width: "90px",
      render: (v) => PRODUCT_TYPE_LABEL[String(v)] ?? String(v ?? "-"),
    },
    {
      key: "items",
      title: "결제 상품명",
      render: (v, row) => {
        const items = v as AdminOrder["items"];
        if (items?.length) {
          return items.length === 1 ? items[0].product_name : `${items[0].product_name} 외 ${items.length - 1}건`;
        }
        return row.product_info?.name ?? "-";
      },
    },
    {
      key: "items",
      title: "결제 수량",
      width: "90px",
      render: (v, row) => {
        const items = v as AdminOrder["items"];
        if (items?.length) {
          return `${items.reduce((s, i) => s + i.quantity, 0)}개`;
        }
        return `${row.quantity ?? 1}개`;
      },
    },
    {
      key: "total_amount",
      title: "결제금액",
      width: "120px",
      render: (v) => `${Number(v).toLocaleString()}원`,
    },
    {
      key: "created_at",
      title: "결제일시",
      width: "130px",
      render: (v) => String(v).slice(0, 16).replace("T", " "),
    },
    {
      key: "status",
      title: "상태",
      width: "100px",
      render: (v, row) => <Badge variant={STATUS_BADGE[String(v)] ?? "draft"}>{row.status_display ?? String(v)}</Badge>,
    },
  ];

  return (
    <div className="admin-page">
      <ContentHeader
        title="결제 관리"
        description="모든 결제 내역을 조회하고 관리합니다."
        actions={[
          <Button key="export" variant="outline">
            엑셀 다운로드
          </Button>,
        ]}
      />

      <div className="stat-cards">
        <div className="stat-card stat-card--primary">
          <div className="stat-card__label">결제 완료 금액</div>
          <div className="stat-card__value">{totalAmount.toLocaleString()}원</div>
          <div className="stat-card__sub">현재 조회 기준</div>
        </div>
        <div className="stat-card stat-card--success">
          <div className="stat-card__label">결제 완료</div>
          <div className="stat-card__value">{paidCount}건</div>
        </div>
        <div className="stat-card stat-card--danger">
          <div className="stat-card__label">환불</div>
          <div className="stat-card__value">{refundedCount}건</div>
        </div>
        <div className="stat-card stat-card--warning">
          <div className="stat-card__label">총 건수</div>
          <div className="stat-card__value">{total}건</div>
        </div>
      </div>

      <div className="admin-page__card">
        <div className="admin-page__filter">
          <Input placeholder="주문번호, 고객명 검색" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSearch()} style={{ width: 260 }} prefix="🔍" />
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            options={[
              { value: "", label: "상태 전체" },
              { value: "CONFIRMED", label: "확정됨" },
              { value: "COMPLETED", label: "완료됨" },
              { value: "PENDING", label: "결제 대기" },
              { value: "PAYMENT_FAILED", label: "결제 실패" },
              { value: "CANCELLED", label: "취소됨" },
            ]}
            style={{ width: 140 }}
          />
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #E4E4E7",
                borderRadius: 8,
                fontSize: 13,
              }}
            />
            <span style={{ color: "#71717A" }}>~</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #E4E4E7",
                borderRadius: 8,
                fontSize: 13,
              }}
            />
            <Button variant="secondary" size="sm" onClick={handleSearch}>
              조회
            </Button>
          </div>
        </div>

        {loading ? <div style={{ padding: 40, textAlign: "center", color: "#71717A" }}>불러오는 중...</div> : <Table columns={columns} data={orders} rowKey="id" />}
        <Pagination current={currentPage} total={total} pageSize={PAGE_SIZE} onChange={setCurrentPage} />
      </div>
    </div>
  );
};

export default PaymentManagement;
